import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {Box} from "@mui/material";
import AudioPlayer from "./AudioPlayer";
import CallerQueue from "./CallerQueue";
import AudioControls from "./AudioControls";
import AgentStatusBar from "./AgentStatusBar";
import Auth from "./Auth/Auth";
import {
  getRoomDetails,
  getUsersOnCall,
  updateAgentActivityState,
} from "./firebase";
import controls, {
  answerCall,
  endCall,
  prepareAgentMicrophone,
  setAgentCallVolume,
  setOnAuthStateChanged,
} from "./controls";
import {bindWakeLockAutoReacquire} from "./wakeLock";
import {isAnsweredEntry} from "./queueGrouping";
import useReviewerMonitoring from "./useReviewerMonitoring";
import {
  shouldPlayWaitingRingtone,
  syncWaitingRingtone,
} from "./waitingRingtone";

const initialCallStatus = {
  activeCallerId: "",
  callAnswered: false,
  phase: "idle",
  error: "",
  connectionState: "disconnected",
  micPermission: "unknown",
  audioBlocked: false,
  callerMuted: false,
  callerConnected: false,
  callerActive: false,
  callerAudioReady: false,
  callerSpeaking: false,
  agentSpeaking: false,
  callerLevel: 0,
  agentLevel: 0,
  callerNetworkQuality: "unknown",
  agentNetworkQuality: "unknown",
  recordingStatus: "idle",
};

const readCallStatus = () => ({
  activeCallerId: controls.callerIdOnCall || "",
  callAnswered: Boolean(controls.callAnswered),
  phase: controls.callPhase || "idle",
  error: controls.callError || "",
  connectionState: (controls.roomConnectionState || "disconnected").toString().toLowerCase(),
  micPermission: controls.microphonePermission || "unknown",
  audioBlocked: Boolean(controls.audioBlocked),
  callerMuted: Boolean(controls.callerMuted),
  callerConnected: Boolean(controls.callerConnected || controls.callerParticipant),
  callerActive: Boolean(controls.callerActive),
  callerAudioReady: Boolean(
      controls.audioTrackOfCustomer && controls.callerAudioStreamState !== "paused",
  ),
  callerSpeaking: Boolean(controls.callerSpeaking),
  agentSpeaking: Boolean(controls.agentSpeaking),
  callerLevel: controls.callerParticipant?.audioLevel || 0,
  agentLevel: controls.room?.localParticipant?.audioLevel || 0,
  callerNetworkQuality: (controls.callerConnectionQuality || "unknown").toString().toLowerCase(),
  agentNetworkQuality: (controls.agentConnectionQuality || "unknown").toString().toLowerCase(),
  recordingStatus: controls.recordingStatus || "idle",
});

const All = (props) => {
  const [view, setView] = useState("");
  const [queueEntries, setQueueEntries] = useState([]);
  const [selectedCallerId, setSelectedCallerId] = useState("");
  const [highlightedIds, setHighlightedIds] = useState(new Set());
  const [time, setTime] = useState(controls.callDuration);
  const [micOn, setMicOn] = useState(controls.micOn);
  const [callVolume, setCallVolume] = useState(controls.callVolume);
  const [callStatus, setCallStatus] = useState(initialCallStatus);
  const [activityState, setActivityState] = useState("offline");
  const queueIdsRef = useRef([]);
  const resumeAttemptedRef = useRef("");
  const navigableEntries = useMemo(
      () => queueEntries.filter((entry) =>
        !isAnsweredEntry(entry) || entry.id === callStatus.activeCallerId,
      ),
      [queueEntries, callStatus.activeCallerId],
  );
  const reviewerMonitoring = useReviewerMonitoring({
    enabled: view === "call",
    ownCallActive: callStatus.callAnswered ||
      Boolean(callStatus.activeCallerId),
    queueEntries,
  });

  useEffect(() => {
    controls.setView = setView;
    controls.setTime = setTime;
    controls.setMicOn = setMicOn;
    const unsubscribeAuth = setOnAuthStateChanged();
    return () => unsubscribeAuth?.();
  }, []);

  const handleUserChange = useCallback((doc) => {
    const usersOnQueue = doc?.usersOnQueue || [];
    const previousQueueIds = queueIdsRef.current;
    const newlyAdded = usersOnQueue.filter((id) => !previousQueueIds.includes(id));
    queueIdsRef.current = usersOnQueue;

    if (
      controls.callAnswered &&
      (controls.callerIdOnCall || "").length > 2 &&
      !usersOnQueue.includes(controls.callerIdOnCall)
    ) {
      endCall(setMicOn, setTime, controls.callerIdOnCall, "remote");
    }

    if (newlyAdded.length > 0) {
      setHighlightedIds((previous) => {
        const next = new Set(previous);
        newlyAdded.forEach((id) => next.add(id));
        return next;
      });
      newlyAdded.forEach((id) => {
        setTimeout(() => {
          setHighlightedIds((previous) => {
            const next = new Set(previous);
            next.delete(id);
            return next;
          });
        }, 1800);
      });
    }

    const nextEntries = usersOnQueue.map((id, index) => ({
      id,
      answeredBy: doc[id] || "",
      queueNumber: index + 1,
      timeRing: Number(doc[`${id}-timeRing`] || 0),
      timeAnswered: Number(doc[`${id}-timeAnswered`] || 0),
    }));
    setQueueEntries(nextEntries);

    const ownedCall = nextEntries.find((entry) => entry.answeredBy === controls.channelName);
    if (!ownedCall && !controls.callerIdOnCall) resumeAttemptedRef.current = "";
    if (
      ownedCall &&
      !controls.callAnswered &&
      !controls.callerIdOnCall &&
      resumeAttemptedRef.current !== ownedCall.id
    ) {
      resumeAttemptedRef.current = ownedCall.id;
      answerCall(ownedCall.id, setTime, true);
    }
  }, []);

  useEffect(() => {
    if (view !== "call") return undefined;
    let unsubscribeQueue;
    let cancelled = false;

    getRoomDetails().then((allowed) => {
      if (!cancelled && allowed) {
        unsubscribeQueue = getUsersOnCall(handleUserChange);
        updateAgentActivityState("available").then((result) => {
          if (cancelled || !result) return;
          const state = result.state ||
            (result.reason === "active_call" ? "on_call" : "available");
          controls.agentActivityState = state;
          setActivityState(state);
        }).catch(() => {
          if (!cancelled) setActivityState(controls.callAnswered ?
            "on_call" : "available");
        });
      }
    });

    prepareAgentMicrophone().then(() => {
      if (!cancelled) {
        setMicOn(controls.micOn);
        setCallStatus(readCallStatus());
      }
    });

    return () => {
      cancelled = true;
      unsubscribeQueue?.();
    };
  }, [view, handleUserChange]);

  useEffect(() => {
    const cleanup = bindWakeLockAutoReacquire();
    return cleanup;
  }, []);

  useEffect(() => {
    if (view !== "call") return undefined;
    const syncCallState = setInterval(() => {
      setCallStatus(readCallStatus());
      setMicOn(controls.micOn);
      setActivityState(controls.callAnswered ?
        "on_call" : controls.agentActivityState || "available");
    }, document.visibilityState === "visible" ? 260 : 900);
    return () => clearInterval(syncCallState);
  }, [view]);

  const hasWaitingCallers = queueEntries.some(
      (entry) => !isAnsweredEntry(entry),
  );
  const agentCallActive = callStatus.callAnswered ||
    Boolean(callStatus.activeCallerId);
  const reviewerMonitoringActive = reviewerMonitoring.isMonitoring;
  const waitingRingtoneVolume = controls.volume;

  useEffect(() => {
    syncWaitingRingtone({
      player: document.getElementById("myPlayer"),
      volume: waitingRingtoneVolume,
      shouldPlay: shouldPlayWaitingRingtone({
        inCallView: view === "call",
        hasWaitingCallers,
        agentCallActive,
        reviewerMonitoringActive,
      }),
    });
  }, [
    view,
    hasWaitingCallers,
    agentCallActive,
    reviewerMonitoringActive,
    waitingRingtoneVolume,
  ]);

  useEffect(() => {
    if (view !== "call") return undefined;
    const heartbeat = setInterval(() => {
      const state = controls.agentActivityState;
      if (state !== "available" && state !== "break") return;
      updateAgentActivityState(state).catch(() => {});
    }, 4 * 60 * 1000);
    return () => clearInterval(heartbeat);
  }, [view]);

  useEffect(() => {
    if (navigableEntries.length === 0) {
      setSelectedCallerId("");
      return;
    }
    const exists = navigableEntries.some((entry) => entry.id === selectedCallerId);
    if (!exists) setSelectedCallerId(navigableEntries[0].id);
  }, [navigableEntries, selectedCallerId]);

  useEffect(() => {
    if (view !== "call") return undefined;

    const isTypingTarget = (target) => {
      const tag = (target?.tagName || "").toLowerCase();
      return tag === "input" || tag === "textarea" || target?.isContentEditable;
    };
    const clickShortcutTarget = (selector) => {
      const button = document.querySelector(selector);
      if (!button) return false;
      button.click();
      return true;
    };
    const onKeyDown = (event) => {
      if (isTypingTarget(event.target)) return;
      const key = (event.key || "").toLowerCase();

      if (key === "a" && !controls.callAnswered && !controls.callerIdOnCall && queueEntries.length > 0) {
        if (clickShortcutTarget('button[data-role="answer-call"]:not([disabled])')) event.preventDefault();
      }
      if ((key === "arrowdown" || key === "arrowup") && navigableEntries.length > 0) {
        event.preventDefault();
        const currentIndex = navigableEntries.findIndex((entry) => entry.id === selectedCallerId);
        const safeIndex = currentIndex < 0 ? 0 : currentIndex;
        const delta = key === "arrowdown" ? 1 : -1;
        setSelectedCallerId(navigableEntries[(safeIndex + delta + navigableEntries.length) % navigableEntries.length].id);
      }
      if (key === "enter" && selectedCallerId && !controls.callAnswered && !controls.callerIdOnCall) {
        if (clickShortcutTarget(`button[data-role="answer-call"][data-caller-id="${selectedCallerId}"]:not([disabled])`)) event.preventDefault();
      }
      if (key === "e" && controls.callAnswered) {
        if (clickShortcutTarget(`button[data-role="end-call"][data-caller-id="${controls.callerIdOnCall}"]:not([disabled])`)) event.preventDefault();
      }
      if (key === "m" && controls.callAnswered) {
        if (clickShortcutTarget(`button[data-role="mic-toggle"][data-caller-id="${controls.callerIdOnCall}"]:not([disabled])`)) event.preventDefault();
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [view, queueEntries, navigableEntries, selectedCallerId]);

  const queueEntriesWithFlags = useMemo(
      () => queueEntries.map((entry) => ({
        ...entry,
        isHighlighted: highlightedIds.has(entry.id),
        selected: entry.id === selectedCallerId,
      })),
      [queueEntries, highlightedIds, selectedCallerId],
  );

  if (view !== "call") return <Auth setView={setView} view={view} />;

  return (
    <>
      <div hidden><AudioPlayer /></div>
      <Box
        sx={{
          width: "100%",
          height: "100dvh",
          overflow: "hidden",
          display: "grid",
          gridTemplateColumns: {xs: "minmax(0, 1fr)", md: "88px minmax(0, 1fr)"},
          gridTemplateRows: {xs: "auto auto minmax(0, 1fr)", md: "auto minmax(0, 1fr)"},
          background: (theme) => theme.palette.mode === "dark" ?
            "linear-gradient(180deg, #101614 0%, #141c1a 100%)" :
            "linear-gradient(180deg, #f6faf8 0%, #edf4f1 100%)",
        }}
      >
        <Box sx={{gridColumn: 1, gridRow: {xs: 1, md: 2}, minWidth: 0, minHeight: 0}}>
          <AudioControls
            themeMode={props.themeMode}
            toggleThemeMode={props.toggleThemeMode}
            callActive={agentCallActive}
          />
        </Box>
        <Box sx={{gridColumn: {xs: 1, md: 2}, gridRow: {xs: 2, md: 1}, minWidth: 0}}>
          <AgentStatusBar
            queueCount={queueEntries.filter((entry) => !isAnsweredEntry(entry)).length}
            activeCallCount={queueEntries.filter(isAnsweredEntry).length}
            callStatus={callStatus}
            activityState={activityState}
            onActivityChange={async (state) => {
              const previous = activityState;
              setActivityState(state);
              controls.agentActivityState = state;
              try {
                const result = await updateAgentActivityState(state);
                const confirmed = result?.state || state;
                controls.agentActivityState = confirmed;
                setActivityState(confirmed);
              } catch (_) {
                controls.agentActivityState = previous;
                setActivityState(previous);
              }
            }}
            onMicPrepared={() => {
              setMicOn(controls.micOn);
              setCallStatus(readCallStatus());
            }}
          />
        </Box>
        <Box sx={{gridColumn: {xs: 1, md: 2}, gridRow: {xs: 3, md: 2}, minWidth: 0, minHeight: 0}}>
          <CallerQueue
            queueEntries={queueEntriesWithFlags}
            agentName={controls.channelName}
            onTileSelect={setSelectedCallerId}
            callStatus={callStatus}
            time={time}
            micOn={micOn}
            setMicOn={setMicOn}
            setTime={setTime}
            callVolume={callVolume}
            setCallVolume={(value) => {
              setCallVolume(setAgentCallVolume(value));
            }}
            reviewerMonitoring={reviewerMonitoring}
          />
        </Box>
      </Box>
    </>
  );
};

export default All;
